import { type CartItem, type ICartItemFlat, type ProductLightDTO } from 'ecosystem'
import { parseIntSafe } from './numbers'
import { getProductPrice } from './products'
import {
  allocateQuantityDiscount,
  blendedUnitPrice,
  sumQuantityDiscountBlocks
} from './quantityDiscount'

/**
 * A line's total, with the product's quantity ladder applied.
 *
 * The ladder can only be scored once the quantity is known, so it is resolved
 * here rather than baked into `product.price`: writing it back onto the product
 * would compound it on the next quantity change and survive into the persisted
 * cart. A discount code, which is quantity-independent, *is* already folded into
 * `product.price` by `applyDiscountToCart` — so the price this reads is the
 * post-code one, which is exactly the basis the ladder applies to.
 */
export const lineTotal = (
  product: ProductLightDTO,
  quantity: number,
  excludeVat = false
): number => {
  const unitPrice = getProductPrice(product, excludeVat)
  const blocks = allocateQuantityDiscount(quantity, unitPrice, product.quantityDiscount)

  return sumQuantityDiscountBlocks(blocks)
}

/**
 * Walk every cart line at its true quantity, charging each through `priceLine`.
 *
 * Bundles score their ladder against the units actually being bought — the
 * parent quantity times this product's share of one bundle — which is the same
 * flattened quantity `flatCartItems` hands the checkout, so the cart and the
 * order agree by construction.
 */
const walkCart = (
  cartItems: CartItem[],
  priceLine: (product: ProductLightDTO, quantity: number) => number
): number => {
  let total = 0

  cartItems.forEach((item) => {
    const parentQty = parseIntSafe(item.qty) ?? 1

    if (item.type === 'SINGLE') {
      total += priceLine(item.meta.singleType.product, parentQty)
    } else if (item.type === 'GROUP') {
      item.meta.groupType.items.forEach((groupItem) => {
        total += priceLine(groupItem.product, parentQty * (groupItem.originalQty ?? 1))
      })
    }
  })

  return total
}

export const sumCart = (cartItems: CartItem[], excludeVat = false) =>
  walkCart(cartItems, (product, quantity) => lineTotal(product, quantity, excludeVat))

/**
 * The same basket priced **without** any quantity ladder.
 *
 * Only useful against `sumCart`: the gap between the two is what volume pricing
 * saved, which a cart summary reports as its own line. Reporting it as part of
 * the discount-code total would credit the code for a reduction the shopper
 * earned by buying more.
 */
export const sumCartBeforeQuantityDiscount = (cartItems: CartItem[], excludeVat = false) =>
  walkCart(cartItems, (product, quantity) => getProductPrice(product, excludeVat) * quantity)

/**
 * Helper function to get the price from a cart item
 *
 * Quantity-discounted lines report the **blended** unit price — the line total
 * spread across its units — so that a caller multiplying this by the quantity
 * still lands on the figure `sumCart` contributes for the same line. Analytics
 * is the only consumer today, and an item-level value that disagreed with the
 * cart-level one would be the worse answer.
 *
 * @param cartItem - The cart item to extract price from
 * @param excludeVat - When true, returns net (ex-VAT) prices when available
 * @returns The price of the item or 0 if not found
 */
export const getCartItemPrice = (cartItem: CartItem, excludeVat = false): number => {
  if (cartItem.type === 'SINGLE') {
    const quantity = parseIntSafe(cartItem.qty) ?? 1
    return blendedUnitPrice(
      quantity,
      getProductPrice(cartItem.meta.singleType.product, excludeVat),
      cartItem.meta.singleType.product.quantityDiscount
    )
  } else if (cartItem.type === 'GROUP') {
    const parentQty = parseIntSafe(cartItem.qty) ?? 1
    // For group items, sum up all the prices of items in the group
    return cartItem.meta.groupType.items.reduce((total, item) => {
      const itemQty = item.qty || 1
      // Scored against the units actually bought across the whole bundle line,
      // then charged for this one bundle's worth.
      const itemPrice = blendedUnitPrice(
        parentQty * itemQty,
        getProductPrice(item.product, excludeVat),
        item.product.quantityDiscount
      )
      return total + itemPrice * itemQty
    }, 0)
  }
  return 0
}

export const flatCartItems = (cartItems: CartItem[]) => {
  return cartItems
    .map((cartItem): ICartItemFlat[] => {
      if (cartItem.type === 'SINGLE') {
        return [
          {
            cartItemId: cartItem.id,
            qty: cartItem.qty,
            product: cartItem.meta.singleType.product,
            subItems: cartItem.meta.singleType.subItems
          }
        ]
      }

      return cartItem.meta.groupType.items.map((detail) => ({
        cartItemId: cartItem.id,
        qty: (detail.originalQty ?? 1) * (parseIntSafe(cartItem.qty) ?? 1),
        product: detail.product,
        subItems: detail.subItems
      }))
    })
    .flat(1)
}
